import React, { useState, useEffect, useRef, useCallback } from 'react';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import KeywordChip from './KeywordChip';
import { useDebounce } from '../hooks/useDebounce';
import getApiErrorMessage from '../utils/getApiErrorMessage';
import { useToasterStore } from '../stores/ui/toasterStore';

interface KeywordEditorProps {
  tokenAddress: string;
  initialKeywords: string[];
  onSuccess: () => void;
  onClose: () => void;
}

interface KeywordItem {
  keyword: string;
  count: number;
}

const KeywordEditor: React.FC<KeywordEditorProps> = ({
  tokenAddress,
  initialKeywords,
  onSuccess,
  onClose,
}) => {
  const { showToaster } = useToasterStore();
  const [searchInput, setSearchInput] = useState('');
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>(initialKeywords);
  const [keywordList, setKeywordList] = useState<KeywordItem[]>([]);
  const [autocompleteResults, setAutocompleteResults] = useState<KeywordItem[]>([]);
  const [isKeywordListLoading, setIsKeywordListLoading] = useState(false);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const requestIdRef = useRef<number>(0);

  const debouncedSearch = useDebounce(searchInput, 300);

  // initialKeywords가 변경되면 selectedKeywords 업데이트
  useEffect(() => {
    setSelectedKeywords(initialKeywords);
  }, [initialKeywords]);

  // 모달 오픈 시 한 번만 전체 키워드 목록 로드 (getAllKeywords)
  useEffect(() => {
    let isMounted = true;
    const loadAllKeywords = async () => {
      try {
        setIsKeywordListLoading(true);
        const response = await feManagementSystemApi.getAllKeywords(50);
        if (isMounted) {
          setKeywordList(response.data.keywords);
        }
      } catch (error: any) {
        if (isMounted) {
          console.error('Failed to load keywords:', error);
          showToaster(
            getApiErrorMessage(error, 'Failed to load keywords.'),
            'error'
          );
        }
      } finally {
        if (isMounted) {
          setIsKeywordListLoading(false);
        }
      }
    };
    loadAllKeywords();
    return () => {
      isMounted = false;
    };
  }, [showToaster]);

  // 인풋 입력 시 debounce 후 autocomplete API 호출 → 결과는 인풋 밑 드롭다운에만 표시
  useEffect(() => {
    if (debouncedSearch.trim().length < 2) {
      setAutocompleteResults([]);
      return;
    }

    const currentRequestId = ++requestIdRef.current;
    const fetchAutocomplete = async () => {
      try {
        setIsAutocompleteLoading(true);
        const response = await feManagementSystemApi.keywordAutocomplete(debouncedSearch.trim(), 50);
        if (currentRequestId === requestIdRef.current) {
          setAutocompleteResults(response.data ?? []);
        }
      } catch (error: any) {
        if (currentRequestId === requestIdRef.current) {
          console.error('Failed to fetch autocomplete:', error);
          showToaster(
            getApiErrorMessage(error, 'Keyword search failed.'),
            'error'
          );
          setAutocompleteResults([]);
        }
      } finally {
        if (currentRequestId === requestIdRef.current) {
          setIsAutocompleteLoading(false);
        }
      }
    };

    fetchAutocomplete();
  }, [debouncedSearch, showToaster]);

  // 키워드 목록에서 클릭 시 선택/해제 토글
  const handleKeywordClick = useCallback((keyword: string) => {
    setSelectedKeywords((prev) =>
      prev.includes(keyword) ? prev.filter((k) => k !== keyword) : [...prev, keyword]
    );
  }, []);

  // autocomplete 드롭다운에서 선택 시 추가하고 인풋/드롭다운 초기화
  const handleAutocompleteSelect = useCallback((keyword: string) => {
    setSelectedKeywords((prev) =>
      prev.includes(keyword) ? prev : [...prev, keyword]
    );
    setSearchInput('');
    setAutocompleteResults([]);
  }, []);

  // '새로 추가': 입력한 문자열을 새 키워드로 선택에 추가하고 드롭다운 닫기 (제출 시 updateTokenKeywords로 저장됨)
  const handleAddNewKeyword = useCallback(() => {
    const newKeyword = searchInput.trim();
    if (!newKeyword) return;
    setSelectedKeywords((prev) =>
      prev.includes(newKeyword) ? prev : [...prev, newKeyword]
    );
    setSearchInput('');
    setAutocompleteResults([]);
  }, [searchInput]);

  const handleRemoveKeyword = useCallback((keyword: string) => {
    setSelectedKeywords(selectedKeywords.filter(k => k !== keyword));
  }, [selectedKeywords]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      await feManagementSystemApi.updateTokenKeywords(tokenAddress, selectedKeywords);
      showToaster('Keywords updated.', 'success');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to update keywords:', error);
      showToaster(
        getApiErrorMessage(error, 'Failed to update keywords.'),
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // 외부 클릭 시 닫기
  const editorRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (editorRef.current && !editorRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    // 약간의 지연을 두어 현재 클릭 이벤트가 처리된 후에 리스너 추가
    setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  return (
    <div ref={editorRef} className="keyword-editor-container absolute top-full left-0 mt-2 w-96 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 p-4">
      {/* Selected Keywords */}
      <div className="mb-4 min-h-[60px] p-3 bg-gray-900 rounded-md border border-gray-700">
        <div className="text-xs text-gray-400 mb-2">선택된 키워드</div>
        {selectedKeywords.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {selectedKeywords.map((keyword) => (
              <div key={keyword} className="relative group">
                <KeywordChip keyword={keyword} />
                <button
                  onClick={() => handleRemoveKeyword(keyword)}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="제거"
                >
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500">키워드를 선택해주세요</div>
        )}
      </div>
      
      {/* Input + Autocomplete dropdown */}
      <div className="relative mb-4">
        <input
          type="text"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="키워드 검색 (최소 2글자)"
          className="w-full px-3 py-2 bg-gray-900 text-white border border-gray-700 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {searchInput.trim().length >= 2 && (
          <div className="absolute left-0 right-0 top-full mt-1 max-h-[200px] overflow-y-auto rounded-md border border-gray-600 bg-gray-800 shadow-lg z-10 flex flex-col">
            {isAutocompleteLoading ? (
              <div className="px-3 py-3 text-sm text-gray-400 text-center">검색 중...</div>
            ) : (
              <>
                {autocompleteResults.length > 0 ? (
                  <div className="py-1">
                    {autocompleteResults.map((item) => (
                      <button
                        key={item.keyword}
                        type="button"
                        onClick={() => handleAutocompleteSelect(item.keyword)}
                        className="w-full text-left px-3 py-2 text-sm text-white hover:bg-gray-700 transition-colors flex items-center justify-between"
                      >
                        <span>{item.keyword}</span>
                        <span className="text-xs text-gray-500">({item.count})</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="px-3 py-3 text-sm text-gray-400 text-center">검색 결과 없음</div>
                )}
                {!isAutocompleteLoading &&
                  searchInput.trim() &&
                  !autocompleteResults.some(
                    (item) => item.keyword.toLowerCase() === searchInput.trim().toLowerCase()
                  ) && (
                    <div className="mt-0.5 border-t border-gray-600 bg-gray-700/80 px-2 py-2 rounded-b-md">
                      <button
                        type="button"
                        onClick={handleAddNewKeyword}
                        className="w-full px-3 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-500 rounded transition-colors"
                      >
                        새로 추가
                      </button>
                    </div>
                  )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Keyword List (getAllKeywords 결과, 모달 오픈 시 1회 로드) */}
      <div className="mb-4 max-h-[200px] overflow-y-auto">
        <div className="text-xs text-gray-400 mb-2">키워드 목록</div>
        {isKeywordListLoading ? (
          <div className="text-sm text-gray-400 text-center py-4">Loading...</div>
        ) : keywordList.length > 0 ? (
          <div className="space-y-1">
            {keywordList.map((item) => {
              const isSelected = selectedKeywords.includes(item.keyword);
              return (
                <button
                  key={item.keyword}
                  type="button"
                  onClick={() => handleKeywordClick(item.keyword)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                    isSelected
                      ? 'bg-blue-900/50 text-blue-200 hover:bg-blue-900/70 cursor-pointer'
                      : 'bg-gray-900 hover:bg-gray-700 text-white cursor-pointer'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span>{item.keyword}</span>
                    <span className="text-xs text-gray-500">({item.count})</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-sm text-gray-400 text-center py-4">키워드가 없습니다</div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm rounded-md bg-gray-700 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? 'Saving...' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export default KeywordEditor;
